import { motion } from "framer-motion";
import { FOUNDER_IMG } from "@/data/site";
import { Quote } from "lucide-react";

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-40 border-t border-brand-border">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Opening mission line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mb-16 md:mb-24"
        >
          <p className="eyebrow mb-6">(01) — About DigiDoot Solutions</p>
          <p className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-brand-charcoal">
            Every great brand has a <span className="italic text-brand-brown">story</span>. Our mission is to make sure the right people hear it.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Left: image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-white">
              <img
                src={FOUNDER_IMG}
                alt="Chitra Rao — Founder & CEO"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-brand-gradient text-white px-6 py-4 md:px-8 md:py-6 shadow-xl shadow-brand-charcoal/30 ring-1 ring-brand-gold/30">
              <p className="font-display italic text-2xl md:text-3xl leading-tight">Chitra Rao</p>
              <p className="eyebrow text-brand-gold mt-1">Founder & CEO</p>
            </div>
          </motion.div>

          {/* Right: content */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium max-w-[18ch]"
            >
              A creative studio that <span className="italic text-brand-brown">obsesses over your growth</span>.
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="mt-10 space-y-6 text-lg leading-relaxed text-brand-charcoal/80 max-w-2xl"
            >
              <p>
                Founded in 2022 by Chitra Rao, <span className="font-semibold text-brand-charcoal">DigiDoot Solutions</span> is a full-service digital marketing and creative agency dedicated to helping businesses grow through strategy, creativity and performance.
              </p>
              <p>
                We combine data-driven marketing with compelling storytelling to build brands that are not only seen — but remembered.
              </p>
              <p>
                From startups building their identity, to established businesses ready to scale, to government organizations seeking impactful public communication — we create customized marketing solutions that deliver measurable results.
              </p>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-md"
            >
              <div>
                <p className="font-display text-4xl text-brand-brown">2022</p>
                <p className="text-xs uppercase tracking-widest text-brand-muted mt-1">Founded</p>
              </div>
              <div>
                <p className="font-display text-4xl text-brand-brown">50+</p>
                <p className="text-xs uppercase tracking-widest text-brand-muted mt-1">Brands</p>
              </div>
              <div>
                <p className="font-display text-4xl text-brand-brown">15</p>
                <p className="text-xs uppercase tracking-widest text-brand-muted mt-1">Services</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* The "Doot" etymology callout */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-24 md:mt-32 relative bg-brand-brown text-white overflow-hidden grain shadow-xl shadow-brand-charcoal/30"
          data-testid="doot-etymology"
        >
          <div aria-hidden className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-brand-gold/15 blur-3xl" />
          <div aria-hidden className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-brand-brown/20 blur-3xl" />

          <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-16 p-8 md:p-16 lg:p-20">
            <div className="lg:col-span-4">
              <Quote size={40} className="text-brand-gold mb-6" strokeWidth={1.2} />
              <p className="eyebrow text-brand-gold mb-4">The Name</p>
              <p className="font-display text-6xl md:text-7xl lg:text-8xl leading-none tracking-tighter">
                Doot<span className="text-brand-gold">.</span>
              </p>
              <p className="mt-4 text-sm uppercase tracking-[0.28em] text-white/60">
                (Sanskrit) — <span className="italic normal-case font-serif text-base text-white/80">the messenger</span>
              </p>
            </div>

            <div className="lg:col-span-8 flex flex-col justify-center">
              <p className="font-display italic text-2xl md:text-3xl lg:text-[34px] leading-[1.3] text-white text-balance">
                Throughout history, great messengers — Lord Hanuman, Lord Krishna, Prophet Muhammad, Jesus Christ — carried messages that guided, inspired and connected people.
              </p>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-white/70 max-w-2xl">
                Inspired by this timeless idea of meaningful communication, DigiDoot exists to become the messenger for modern brands. In today&apos;s digital world, simply having a great product isn&apos;t enough. Brands need the right message, delivered to the right audience, at the right time.
              </p>
              <p className="mt-8 font-display italic text-xl md:text-2xl text-brand-gold leading-snug max-w-2xl">
                We are more than a digital marketing agency — we are the messenger that connects your brand with the people who matter most.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
